import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ERouteLinks } from 'models/route';
import { Box, Grid, Typography } from '@mui/material';
import { useTranslations } from 'hooks/useTranslations';
import { useForm } from 'react-hook-form';
import Button from 'components/atoms/Button';
import { EButtonVariants } from 'constants/Buttons';
import {
  IPrintoutSettings,
  IPrintoutSettingsConditions,
  IPrintoutSettingsGeneral,
  IPrintoutSettingsOwner,
  IPrintoutSettingsWeaponData,
} from 'models/licenceConfigurator';
import { useConfigurationLicence } from '../hooks';
import { ELicenceCardInformationFormLabels } from '../helper';
import Breadcrumbs from 'components/atoms/Breadcrumbs';
import { StyledPageTitle } from 'theme/styles';
import { useAlert } from 'models/alertContext';
import { Alert } from 'components/atoms/Alert';
import FormLabel from 'components/atoms/FormLabel';
import { FormCheckbox } from 'components/molecules/FormCheckbox';
import { Loader } from 'components/atoms/Loader';
import { ActionTypes } from 'state/actions/alert';
import { StyledLink } from './style';
import { alignItems } from '@mui/system';

const initialFormValues: IPrintoutSettings = {
  general: {
    licenceName: false,
    licenceCode: false,
    licenceNumber: false,
    licencePeriod: false,
    issueDate: false,
    issuedLicencingAuthorityName: false,
    issuedLicencingAuthorityPerson: false,
    issuedLicencingAuthoritySignature: false,
  },
  licenceOwner: {
    legalType: false,
    fullName: false,
    primaryAddress: false,
    gender: false,
    documentTypeAndName: false,
    dateOfBirth: false,
    placeOfBirth: false,
    nationality: false,
    height: false,
    occupation: false,
    photo: false,
    organisationType: false,
    organisationName: false,
    departmentName: false,
    secondSurname: false,
  },
  conditions: {
    areaRestriction: false,
    competency: false,
    safeStorage: false,
    weaponCategorisation: false,
    weaponNumberLimit: false,
    carryConditions: false,
    statutoryAndAdditionalConditions: false,
    goodReasons: false,
  },
  weaponData: {
    type: false,
    make: false,
    model: false,
    calibre: false,
    action: false,
    manufacturer: false,
    serialNumber: false,
  },
};

const LicencePrintoutSettingsEdit = () => {
  const navigate = useNavigate();
  const { clearAlert, samePageAlert } = useAlert();
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingInitData, setIsLoadingInitData] = useState(false);
  const { t } = useTranslations();
  const { id } = useParams<{ id: string }>();
  const {
    getLicence,
    licence,
    addPrintoutSettings,
    editPrintoutSettings,
    preparePrintoutSettingsFields,
    generalFields,
    conditionsFields,
    ownerFields,
    weaponDataFields,
  } = useConfigurationLicence();

  const [allCheckedGeneral, setAllCheckedGeneral] = useState(false);
  const [allCheckedConditions, setAllCheckedConditions] = useState(false);
  const [allCheckedLicenceOwner, setAllCheckedLicenceOwner] = useState(false);
  const [allCheckedLicenceWeaponData, setAllCheckedLicenceWeaponData] = useState(false);

  useEffect(
    () => () => {
      clearAlert(ActionTypes.CLEAR_SAME_PAGE_ALERT);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm<IPrintoutSettings>({
    defaultValues: initialFormValues,
  });

  useEffect(() => {
    (async function getDataInit() {
      setIsLoadingInitData(true);
      if (id) {
        getLicence(id);
      }
      setIsLoadingInitData(false);
    })();
  }, [id]);

  const filtered = (object: any, items: string[]) => {
    return Object.keys(object)
      .filter((key) => items.includes(key))
      .reduce((obj: Record<string, boolean>, key) => {
        const value = object[key as keyof IPrintoutSettingsGeneral];
        return {
          ...obj,
          [key]: value,
        };
      }, {});
  };

  const allChecked = (object: any, items: string[]): boolean => {
    const filteredObject = filtered(object, items);
    return !Object.values(filteredObject).filter((item: boolean) => !item).length;
  };

  useEffect(() => {
    if (licence && licence.printoutSettings) {
      reset(licence.printoutSettings);
      if (conditionsFields.length) {
        setAllCheckedConditions(
          allChecked(licence?.printoutSettings.conditions, conditionsFields),
        );
      }
      if (generalFields.length) {
        setAllCheckedGeneral(allChecked(licence?.printoutSettings.general, generalFields));
      }
      if (ownerFields.length) {
        setAllCheckedLicenceOwner(
          allChecked(licence?.printoutSettings.licenceOwner, ownerFields),
        );
      }
      if (weaponDataFields.length) {
        setAllCheckedLicenceWeaponData(
          allChecked(licence?.printoutSettings.weaponData, weaponDataFields),
        );
      }
    }
  }, [licence, conditionsFields, generalFields, ownerFields, weaponDataFields]);

  useEffect(() => {
    if (id) {
      preparePrintoutSettingsFields(id);
    }
  }, [id]);

  const onSubmitForm = async (data: IPrintoutSettings) => {
    if (id) {
      let res = false;
      setIsLoading(true);
      if (licence?.printoutSettings) {
        res = await editPrintoutSettings(id, data);
      } else {
        res = await addPrintoutSettings(id, data);
      }
      setIsLoading(false);
      if (res) {
        navigate(ERouteLinks.LicenceTemplatePage.replace(':id', id));
      }
    }
  };

  const checkAll = (section: string, clear: boolean = false) => {
    if (section === 'general') {
      // eslint-disable-next-line array-callback-return
      generalFields?.map((value: string) => {
        setValue(
          `general.${value}` as `general.${keyof IPrintoutSettingsGeneral}`,
          !allCheckedGeneral,
        );
      });
      setAllCheckedGeneral(!allCheckedGeneral);
    }
    if (section === 'conditions') {
      // eslint-disable-next-line array-callback-return
      conditionsFields?.map((value: string) => {
        setValue(
          `conditions.${value}` as `conditions.${keyof IPrintoutSettingsConditions}`,
          !allCheckedConditions,
        );
      });
      setAllCheckedConditions(!allCheckedConditions);
    }
    if (section === 'licenceOwner') {
      // eslint-disable-next-line array-callback-return
      ownerFields?.map((value: string) => {
        setValue(
          `licenceOwner.${value}` as `licenceOwner.${keyof IPrintoutSettingsOwner}`,
          !allCheckedLicenceOwner,
        );
      });
      setAllCheckedLicenceOwner(!allCheckedLicenceOwner);
    }
    if (section === 'licenceWeaponData') {
      // eslint-disable-next-line array-callback-return
      weaponDataFields?.map((value: string) => {
        setValue(
          `weaponData.${value}` as `weaponData.${keyof IPrintoutSettingsWeaponData}`,
          !allCheckedLicenceWeaponData,
        );
      });
      setAllCheckedLicenceWeaponData(!allCheckedLicenceWeaponData);
    }
  };

  const noOptionsSelectedText = (
    <Box sx={{ width: '100%' }}>
      <Typography sx={{ fontStyle: 'italic' }}>
        {t('licenceDetails.noOptionsSelected.text')}
      </Typography>
    </Box>
  );

  return (
    <>
      <Breadcrumbs
        items={[
          {
            label: t('module.administration.name'),
            route: ERouteLinks.Administration,
          },
          {
            label: t('licenceConfiguration.header'),
            route: ERouteLinks.LicenceConfigurationPage,
          },
          {
            label: t('licenceConfiguration.detail.header'),
            route: ERouteLinks.LicenceTemplatePage.replace(':id', id || ''),
          },
          t('licence.editLicencePrintoutSettings.label'),
        ]}
      />

      <StyledPageTitle variant="h4">
        {licence?.printoutSettings
          ? t('licence.editLicencePrintoutSettings.label')
          : t('licence.addLicencePrintoutSettings.label')}
      </StyledPageTitle>
      <Typography sx={{ padding: '12px 0 20px 0' }} variant="body2">
        {t('licence.licencePrintoutSettings.text')}
      </Typography>

      {isLoadingInitData ? (
        <Loader isLoading />
      ) : (
        <form onSubmit={handleSubmit(onSubmitForm)}>
          <Grid container spacing="20px">
            <Grid container size={{ xs: 12 }} columnSpacing="20px">
              <Grid size={{ xs: 12, md: 6, xl: 4 }}>
                <Grid
                  container
                  size={{ xs: 12 }}
                  style={{ columnGap: '8px', display: 'flex', alignItems: 'center' }}
                >
                  <Typography variant="subtitle2">
                    {t('licence.generalLicenceInformation.label')}
                  </Typography>
                  {!!generalFields?.length && (
                    <StyledLink variant="body2" onClick={() => checkAll('general')}>
                      {allCheckedGeneral
                        ? t('global.unselect.label')
                        : t('global.selectAll.label')}
                    </StyledLink>
                  )}
                </Grid>
                {generalFields?.length === 0 && noOptionsSelectedText}
                {generalFields?.map((item: string) => (
                  <FormCheckbox
                    name={`general.${item}`}
                    label={t(
                      ELicenceCardInformationFormLabels?.[
                        item as keyof typeof ELicenceCardInformationFormLabels
                      ],
                    )}
                    control={control}
                    errors={errors}
                  />
                ))}
              </Grid>
              <Grid size={{ xs: 12, md: 6, xl: 4 }}>
                <Grid
                  size={{ xs: 12 }}
                  style={{ columnGap: '8px', display: 'flex', alignItems: 'center' }}
                >
                  <Typography variant="subtitle2">
                    {t('licence.licenceConditions.label')}
                  </Typography>
                  {!!conditionsFields?.length && (
                    <StyledLink variant="body2" onClick={() => checkAll('conditions')}>
                      {allCheckedConditions
                        ? t('global.unselect.label')
                        : t('global.selectAll.label')}
                    </StyledLink>
                  )}
                </Grid>
                {conditionsFields?.length === 0 && noOptionsSelectedText}
                {conditionsFields?.map((item: string) => (
                  <FormCheckbox
                    name={`conditions.${item}`}
                    label={t(
                      ELicenceCardInformationFormLabels?.[
                        item as keyof typeof ELicenceCardInformationFormLabels
                      ],
                    )}
                    control={control}
                    errors={errors}
                  />
                ))}
              </Grid>
            </Grid>
            <Grid container size={{ xs: 12 }} columnSpacing="20px">
              <Grid size={{ xs: 12, md: 6, xl: 4 }}>
                <Grid
                  size={{ xs: 12 }}
                  style={{ columnGap: '8px', display: 'flex', alignItems: 'center' }}
                >
                  <Typography variant="subtitle2">
                    {t('licence.licenceOwner.label')}
                  </Typography>
                  {!!ownerFields?.length && (
                    <StyledLink variant="body2" onClick={() => checkAll('licenceOwner')}>
                      {allCheckedLicenceOwner
                        ? t('global.unselect.label')
                        : t('global.selectAll.label')}
                    </StyledLink>
                  )}
                </Grid>
                {ownerFields?.length === 0 && noOptionsSelectedText}
                {ownerFields?.map((item: string) => (
                  <FormCheckbox
                    name={`licenceOwner.${item}`}
                    label={t(
                      ELicenceCardInformationFormLabels?.[
                        item as keyof typeof ELicenceCardInformationFormLabels
                      ],
                    )}
                    control={control}
                    errors={errors}
                  />
                ))}
              </Grid>
              <Grid size={{ xs: 12, md: 6, xl: 4 }}>
                <Grid
                  size={{ xs: 12 }}
                  style={{ columnGap: '8px', display: 'flex', alignItems: 'center' }}
                >
                  <Typography variant="subtitle2">
                    {t('licence.licenceWeaponData.label')}
                  </Typography>
                  {!!weaponDataFields?.length && (
                    <StyledLink variant="body2" onClick={() => checkAll('licenceWeaponData')}>
                      {allCheckedLicenceWeaponData
                        ? t('global.unselect.label')
                        : t('global.selectAll.label')}
                    </StyledLink>
                  )}
                </Grid>
                {weaponDataFields?.length === 0 && noOptionsSelectedText}
                {weaponDataFields?.map((item: string) => (
                  <FormCheckbox
                    name={`weaponData.${item}`}
                    label={t(
                      ELicenceCardInformationFormLabels?.[
                        item as keyof typeof ELicenceCardInformationFormLabels
                      ],
                    )}
                    control={control}
                    errors={errors}
                  />
                ))}
              </Grid>
            </Grid>
            {samePageAlert && (
              <Grid size={{ xs: 12 }}>
                <Box sx={{ mt: 2 }}>
                  <Alert text={samePageAlert.text} variant={samePageAlert.variant} />
                </Box>
              </Grid>
            )}
            <Grid size={{ xs: 12 }} container columnSpacing="20px" sx={{ mt: 2, rowGap: 1 }}>
              <Grid size={{ xs: 12, sm: 6, lg: 6, xl: 3 }}>
                <Button
                  type="submit"
                  fullWidth
                  label={t('general.save.button')}
                  id="save-button"
                  variant={EButtonVariants.contained}
                  isLoading={isLoading}
                  disabled={isLoading}
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 6, lg: 6, xl: 3 }}>
                <Button
                  fullWidth
                  label={t('general.cancel.button')}
                  id="cancel-button"
                  variant={EButtonVariants.outlined}
                  onClick={() =>
                    navigate(ERouteLinks.LicenceTemplatePage.replace(':id', id || ''))
                  }
                  disabled={isLoading}
                />
              </Grid>
            </Grid>

            <Grid size={{ xs: 12 }} sx={{ mt: 2 }}>
              <Typography variant="caption">{t('general.mandatoryField.text')}</Typography>
            </Grid>
          </Grid>
        </form>
      )}
    </>
  );
};

export { LicencePrintoutSettingsEdit };
